import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';

import {
  SupplierDetailForm,
  SupplierDetailResponse,
  NoharaRepresentative,
} from '../../types/company.types';
import { validateSupplierDetailForm } from '../../utils/validations/supplier-validation';
import {
  loadSupplierDataFromApi,
  mappingEditSupplierData,
} from '../../utils/mappers/supplier-form-mapper';
import { companiesService } from '../../services/companies.service';
import { useDepartments } from '../use-departments';
import { useStaffs } from '../use-staffs';
import { getStaffsFromDepartment } from '../../utils/common-staff-utils';
import { useMarkInitialDetailFieldsTouched } from '../common/use-mark-initial-detail-fields-touched';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { FunctionName, PermissionAction } from '@/lib/enums';
import { getUniqueDepartmentContactLists } from '@/utils/helpers';

const initialFormData: SupplierDetailForm = {
  galiSupplierCode: '',
  officialName: '',
  abbreviatedName: '',
  branchDepartment: '',
  supplierNameKatakana: '',
  supplierType: undefined,
  supplierCategory: undefined,
  currentAddress: {
    postalCode1: '',
    postalCode2: '',
    address1: '',
    address2: '',
    address3: '',
    phone: '',
    fax: '',
  },
  headOfficeAddress: {
    postalCode1: '',
    postalCode2: '',
    address1: '',
    address2: '',
    address3: '',
  },
  representatives: [
    {
      department: '',
      departmentCode: '',
      salesPerson: {
        id: '',
        firstName: '',
        lastName: '',
        code: '',
      },
    },
  ],
  factoryName: '',
  factoryAddress: '',
  factoryPostalCode1: '',
  factoryPostalCode2: '',
  factoryContacts: [],
  certificationInfo: {
    jisNumber: '',
    certificationType: undefined,
    highStrengthNumber: '',
  },
  unionMembership: {
    industrialUnion: '',
    cooperativeUnions: [],
  },
  qualifiedPersonnel: {
    professionalEngineers: '',
    concreteDiagnosticians: '',
    seniorConcreteTechnicians: '',
    concreteTechnicians: '',
  },
  rawMaterials: {
    cement: [],
    coarseAggregate: [],
    fineAggregate: [],
  },
  mixerInfo: {
    ratedCapacity: '',
    type: '',
    mixerCapacity: '',
    mixerCardinality: '',
    manufacturer: '',
    measurementMethod: '',
  },
  transportVehicles: {
    large10t: '',
    medium8t: '',
    size7t: '',
    size5t: '',
    small4t: '',
    size3t: '',
    size2t: '',
    other: '',
  },
  isCommonCement: false,
};

export const useSupplierDetailForm = () => {
  const { supplierId } = useParams<{ supplierId: string }>();

  // Form state
  const [formData, setFormData] = useState<SupplierDetailForm>(initialFormData);
  const [originalFormData, setOriginalFormData] = useState<SupplierDetailForm>(initialFormData);
  const [supplierData, setSupplierData] = useState<SupplierDetailResponse | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Loading and error states
  const [error, setError] = useState<string | null>(null);
  const { isLoading, setIsLoading } = useLoadingProgress();
  const [activeSection, setActiveSection] = useState('company-registration');

  // Refs
  const hasFetchedData = useRef(false);

  // Global state hooks
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.UPDATE);
  const { staffsByDepartment, loading: staffsLoading, loadStaffsByDepartment } = useStaffs();

  // Validation
  const validation = useMemo(() => {
    const result = validateSupplierDetailForm(formData);
    return result;
  }, [formData, touchedFields]);

  // Check if form has changes
  const hasFormChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Form field update function
  const updateField = (field: keyof SupplierDetailForm, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouchedFields((prev) => new Set(prev).add(String(field)));
  };

  // Address management
  const updateCurrentAddress = (
    field: keyof SupplierDetailForm['currentAddress'],
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      currentAddress: { ...prev.currentAddress, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`currentAddress.${field}`));
  };

  // Auto-pad postal codes on blur
  const handleCurrentAddressBlur = (
    field: keyof SupplierDetailForm['currentAddress'],
    value: string
  ) => {
    if (field === 'postalCode1' && value && /^\d{1,3}$/.test(value)) {
      const paddedValue = value.padStart(3, '0');
      setFormData((prev) => ({
        ...prev,
        currentAddress: { ...prev.currentAddress, [field]: paddedValue },
      }));
    } else if (field === 'postalCode2' && value && /^\d{1,4}$/.test(value)) {
      const paddedValue = value.padStart(4, '0');
      setFormData((prev) => ({
        ...prev,
        currentAddress: { ...prev.currentAddress, [field]: paddedValue },
      }));
    }
  };

  const updateHeadOfficeAddress = (
    field: keyof SupplierDetailForm['headOfficeAddress'],
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      headOfficeAddress: { ...prev.headOfficeAddress, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`headOfficeAddress.${field}`));
  };

  // Auto-pad postal codes on blur
  const handleHeadOfficeAddressBlur = (
    field: keyof SupplierDetailForm['headOfficeAddress'],
    value: string
  ) => {
    if (field === 'postalCode1' && value && /^\d{1,3}$/.test(value)) {
      const paddedValue = value.padStart(3, '0');
      setFormData((prev) => ({
        ...prev,
        headOfficeAddress: { ...prev.headOfficeAddress, [field]: paddedValue },
      }));
    } else if (field === 'postalCode2' && value && /^\d{1,4}$/.test(value)) {
      const paddedValue = value.padStart(4, '0');
      setFormData((prev) => ({
        ...prev,
        headOfficeAddress: { ...prev.headOfficeAddress, [field]: paddedValue },
      }));
    }
  };

  // Factory field update helpers
  const updateFactoryPostalCode1 = (value: string) => {
    setFormData((prev) => ({ ...prev, factoryPostalCode1: value }));
    setTouchedFields((prev) => new Set(prev).add('factoryAddress.postalCode1'));
  };

  const updateFactoryPostalCode2 = (value: string) => {
    setFormData((prev) => ({ ...prev, factoryPostalCode2: value }));
    setTouchedFields((prev) => new Set(prev).add('factoryAddress.postalCode2'));
  };

  // Auto-pad factory postal codes on blur
  const handleFactoryPostalCode1Blur = (value: string) => {
    if (value && /^\d{1,3}$/.test(value)) {
      const paddedValue = value.padStart(3, '0');
      setFormData((prev) => ({ ...prev, factoryPostalCode1: paddedValue }));
    }
  };

  const handleFactoryPostalCode2Blur = (value: string) => {
    if (value && /^\d{1,4}$/.test(value)) {
      const paddedValue = value.padStart(4, '0');
      setFormData((prev) => ({ ...prev, factoryPostalCode2: paddedValue }));
    }
  };

  const updateFactoryContact = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      factoryContacts: prev.factoryContacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ),
    }));
    setTouchedFields((prev) => new Set(prev).add(`factoryContacts.${index}.${field}`));
  };

  // Certification update helper
  const updateCertification = (field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      certificationInfo: { ...prev.certificationInfo, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`certificationInfo.${field}`));
  };

  // Union membership update helpers
  const updateIndustrialUnion = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      unionMembership: { ...prev.unionMembership, industrialUnion: value },
    }));
    setTouchedFields((prev) => new Set(prev).add('unionMembership.industrialUnion'));
  };

  const updateCooperativeUnion = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      unionMembership: {
        ...prev.unionMembership,
        cooperativeUnions: prev.unionMembership.cooperativeUnions.map((union, i) =>
          i === index ? { ...union, [field]: value } : union
        ),
      },
    }));
    setTouchedFields((prev) =>
      new Set(prev).add(`unionMembership.cooperativeUnions.${index}.${field}`)
    );
  };

  // Qualified personnel update helper
  const updateQualifiedPersonnel = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      qualifiedPersonnel: { ...prev.qualifiedPersonnel, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`qualifiedPersonnel.${field}`));
  };

  // Mixer info update helper
  const updateMixerInfo = (field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      mixerInfo: { ...prev.mixerInfo, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`mixerInfo.${field}`));
  };

  // Transport vehicles update helper
  const updateTransportVehicles = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      transportVehicles: { ...prev.transportVehicles, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`transportVehicles.${field}`));
  };

  // Raw materials update helper
  const updateRawMaterial = (
    category: 'cement' | 'coarseAggregate' | 'fineAggregate',
    index: number,
    field: string,
    value: unknown
  ) => {
    setFormData((prev) => ({
      ...prev,
      rawMaterials: {
        ...prev.rawMaterials,
        [category]: prev.rawMaterials[category].map((material, i) =>
          i === index ? { ...material, [field]: value } : material
        ),
      },
    }));
    setTouchedFields((prev) => new Set(prev).add(`rawMaterials.${category}.${index}.${field}`));
  };

  // Add raw material
  const addRawMaterial = (category: 'cement' | 'coarseAggregate' | 'fineAggregate') => {
    const newMaterial = {
      type: '',
      manufacturerOrOrigin: '',
      hasLimestone: false,
    };
    setFormData((prev) => ({
      ...prev,
      rawMaterials: {
        ...prev.rawMaterials,
        [category]: [...prev.rawMaterials[category], newMaterial],
      },
    }));
  };

  // Remove raw material
  const removeRawMaterial = (
    category: 'cement' | 'coarseAggregate' | 'fineAggregate',
    index: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      rawMaterials: {
        ...prev.rawMaterials,
        [category]: prev.rawMaterials[category].filter((_, i) => i !== index),
      },
    }));
  };

  // Add cooperative union
  const addCooperativeUnion = () => {
    const newUnion = {
      id: Date.now().toString(),
      name: '',
    };
    setFormData((prev) => ({
      ...prev,
      unionMembership: {
        ...prev.unionMembership,
        cooperativeUnions: [...prev.unionMembership.cooperativeUnions, newUnion],
      },
    }));
  };

  // Remove cooperative union
  const removeCooperativeUnion = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      unionMembership: {
        ...prev.unionMembership,
        cooperativeUnions: prev.unionMembership.cooperativeUnions.filter((_, i) => i !== index),
      },
    }));
  };

  // Add factory contact
  const addFactoryContact = () => {
    const newContact = {
      id: Date.now().toString(),
      roleType: '営業' as const,
      contactPerson: '',
      department: '',
      phone: '',
      email: '',
      fax: '',
    };
    setFormData((prev) => ({
      ...prev,
      factoryContacts: [...prev.factoryContacts, newContact],
    }));
  };

  // Remove factory contact
  const removeFactoryContact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      factoryContacts: prev.factoryContacts.filter((_, i) => i !== index),
    }));
  };

  // Copy current address to head office (excluding phone and fax)
  const copyCurrentToHeadOffice = () => {
    setFormData((prev) => ({
      ...prev,
      headOfficeAddress: {
        postalCode1: prev.currentAddress.postalCode1,
        postalCode2: prev.currentAddress.postalCode2,
        address1: prev.currentAddress.address1,
        address2: prev.currentAddress.address2,
        address3: prev.currentAddress.address3,
        // phone and fax are not copied
      },
    }));
    setTouchedFields((prev) => new Set(prev).add('headOfficeAddress'));
  };

  // Representative management
  const addRepresentative = () => {
    if (formData.representatives.length < 10) {
      const newRepresentative: NoharaRepresentative = {
        department: '',
        departmentCode: '',
        salesPerson: {
          id: '',
          firstName: '',
          lastName: '',
          code: '',
        },
      };
      const newIndex = formData.representatives.length;

      setFormData((prev) => {
        const updated = {
          ...prev,
          representatives: [...prev.representatives, newRepresentative],
        };
        return updated;
      });

      setTouchedFields((prev) => {
        const newTouched = new Set(prev);
        newTouched.add(`representatives.${newIndex}.department`);
        newTouched.add(`representatives.${newIndex}.salesPerson`);
        return newTouched;
      });
    }
  };

  const removeRepresentative = (id: string) => {
    const repIndex = Number.parseInt(id);
    if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= formData.representatives.length)
      return;

    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.filter((_, index) => index !== repIndex),
    }));

    setTouchedFields((prev) => {
      const newTouched = new Set<string>();
      prev.forEach((field) => {
        if (
          field === `representatives.${repIndex}.department` ||
          field === `representatives.${repIndex}.salesPerson`
        ) {
          return;
        }
        const match = field.match(/^representatives\.(\d+)\.(.+)$/);
        if (match) {
          const fieldIndex = Number.parseInt(match[1]);
          const fieldName = match[2];
          if (fieldIndex > repIndex) {
            newTouched.add(`representatives.${fieldIndex - 1}.${fieldName}`);
          } else {
            newTouched.add(field);
          }
        } else {
          newTouched.add(field);
        }
      });
      return newTouched;
    });
  };

  const updateRepresentative = (id: string, field: keyof NoharaRepresentative, value: string) => {
    const repIndex = Number.parseInt(id);
    if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= formData.representatives.length)
      return;

    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.map((rep, index) => {
        if (index === repIndex) {
          if (field === 'department') {
            return {
              ...rep,
              [field]: value,
              salesPerson: {
                id: '',
                firstName: '',
                lastName: '',
                code: '',
              },
            };
          }
          return { ...rep, [field]: value };
        }
        return rep;
      }),
    }));

    setTouchedFields((prev) => new Set(prev).add(`representatives.${repIndex}.${field}`));
  };

  // Utility functions
  const getFieldError = (fieldName: string): string | undefined => {
    // Only show error if field has been touched
    if (!touchedFields.has(fieldName)) {
      return undefined;
    }
    const error = validation.errors.find((error) => error.field === fieldName);
    const errorMessage = error?.message;

    return errorMessage;
  };

  const isFieldValid = (fieldName: string): boolean => {
    // Only show error if field has been touched
    if (!touchedFields.has(fieldName)) return true;
    return !validation.errors.some((error) => error.field === fieldName);
  };

  const getSalesPersonOptions = (departmentId: string) => {
    return getStaffsFromDepartment(staffsByDepartment, departmentId);
  };

  const handleDepartmentChange = async (departmentId: string) => {
    if (departmentId) {
      try {
        await loadStaffsByDepartment('sale', departmentId, FunctionName.COMPANY_MANAGEMENT);
      } catch (error) {
        console.error('Error loading staffs:', error);
      }
    }
  };

  // Data fetching
  const fetchSupplierData = useCallback(async () => {
    if (hasFetchedData.current) return;

    try {
      setIsLoading(true);
      hasFetchedData.current = true;

      if (!supplierId) {
        throw new Error('Supplier ID is required');
      }

      const response = await companiesService.getSupplierById(supplierId);
      const supplierData = (response as { data: { data: SupplierDetailResponse } }).data.data;
      setSupplierData(supplierData);
    } catch (err) {
      console.error('Error fetching supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to load supplier data');
    }
  }, [supplierId, setSupplierData, setIsLoading]);

  // Extract preload staffs logic to reduce nesting
  const preloadStaffsForDepartments = useCallback(
    async (departmentContactLists: SupplierDetailResponse['departmentContactLists']) => {
      if (!departmentContactLists || departmentContactLists.length === 0) {
        return;
      }

      const uniqueDepartments = getUniqueDepartmentContactLists(departmentContactLists);

      const loadStaffsPromises = uniqueDepartments.map(async (department) => {
        if (!department.departmentId || !department.canUpdate) {
          return;
        }

        try {
          await loadStaffsByDepartment(
            'sale',
            department.departmentId,
            FunctionName.COMPANY_MANAGEMENT
          );
        } catch (error) {
          console.error(
            `Error preloading staffs for department ${department.departmentId}:`,
            error
          );
        }
      });

      await Promise.all(loadStaffsPromises);
    },
    [departments, loadStaffsByDepartment]
  );

  // Extract mapping logic to reduce nesting
  const mapSupplierDataToForm = useCallback(
    async (data: SupplierDetailResponse) => {
      await preloadStaffsForDepartments(data.departmentContactLists);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const mappedData = loadSupplierDataFromApi(data, departments);
      setFormData(mappedData);
      setOriginalFormData(mappedData);
      setIsLoading(false);
    },
    [departments, preloadStaffsForDepartments]
  );

  useEffect(() => {
    if (!supplierData) return;
    void mapSupplierDataToForm(supplierData);
  }, [supplierData, mapSupplierDataToForm]);

  useEffect(() => {
    setIsLoading(true);
    if (supplierId && !hasFetchedData.current) {
      void fetchSupplierData();
    }
  }, [supplierId, fetchSupplierData, setIsLoading]);

  // Mark required fields as touched & log initial errors after data is loaded
  useMarkInitialDetailFieldsTouched({
    formData,
    touchedFields,
    setTouchedFields,
    hasFetchedData,
    validateForm: validateSupplierDetailForm,
  });

  // Handle form submission
  const handleSubmit = async (): Promise<boolean> => {
    // Mark all fields as touched to show validation errors
    const allFieldNames = new Set([
      'galiSupplierCode',
      'officialName',
      'abbreviatedName',
      'supplierNameKatakana',
      'supplierType',
      'supplierCategory',
      'currentAddress.postalCode1',
      'currentAddress.postalCode2',
      'currentAddress.address1',
      'currentAddress.address2',
      'currentAddress.phone',
      'currentAddress.fax',
      'headOfficeAddress.postalCode1',
      'headOfficeAddress.postalCode2',
      'headOfficeAddress.address1',
      'headOfficeAddress.address2',
      ...formData.representatives.flatMap((_, index) => [
        `representatives.${index}.department`,
        `representatives.${index}.salesPerson`,
      ]),
    ]);
    setTouchedFields(allFieldNames);

    setIsLoading(true);

    try {
      if (!supplierId) {
        setError('仕入先IDが必要です。');
        return false;
      }

      const validationResult = validateSupplierDetailForm(formData);

      if (!validationResult.isValid) {
        setError('入力内容にエラーがあります。確認してください。');
        return false;
      }

      const apiRequest = mappingEditSupplierData(formData, departments);
      const response = await companiesService.updateSupplierById(supplierId, apiRequest);

      if (response.status === 200 || response.status === 201) {
        setOriginalFormData(formData);
        setTouchedFields(new Set());
        setError(null);
        return true;
      } else {
        setError('更新に失敗しました。もう一度お試しください。');
        return false;
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      setError('更新に失敗しました。もう一度お試しください。');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(originalFormData);
    setTouchedFields(new Set());
    setError(null);
  };

  const canAddRepresentative = formData.representatives.length < 10;
  const isSubmitDisabled = !validation.isValid || !hasFormChanges || isLoading;

  return {
    // Data
    supplierId,
    formData,
    originalFormData,

    // State
    touchedFields,
    isDeleting,
    isLoading,
    error,
    activeSection,
    setIsDeleting,
    setActiveSection,
    setError,

    // Validation
    validation,
    hasFormChanges,

    // Form actions
    updateField,
    updateCurrentAddress,
    updateHeadOfficeAddress,
    copyCurrentToHeadOffice,
    updateFactoryPostalCode1,
    updateFactoryPostalCode2,

    // Blur handlers for auto-padding
    handleCurrentAddressBlur,
    handleHeadOfficeAddressBlur,
    handleFactoryPostalCode1Blur,
    handleFactoryPostalCode2Blur,
    updateFactoryContact,
    updateCertification,
    updateIndustrialUnion,
    updateCooperativeUnion,
    updateQualifiedPersonnel,
    updateMixerInfo,
    updateTransportVehicles,
    updateRawMaterial,
    addRawMaterial,
    removeRawMaterial,
    addCooperativeUnion,
    removeCooperativeUnion,
    addFactoryContact,
    removeFactoryContact,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    resetForm,

    // Utility functions
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    handleDepartmentChange,
    setIsLoading,
    // Computed properties
    canAddRepresentative,
    isSubmitDisabled,

    // External data
    departments,
    departmentsLoading,
    staffsLoading,
    staffsByDepartment,
  };
};
